/**
 * Canonical gear-slot model, shared by the React app and the Express server.
 *
 * Three things used to be conflated across the codebase, which is what made
 * rings and off-hands a permanent source of special cases:
 *
 *   item slot  - what an item claims to be ("FINGER", "TWOHWEAPON", "SHIELD")
 *   socket     - a physical position on the character (ring 1, off hand)
 *   slot group - where a player's intent lives ("my rings", "my weapons")
 *
 * A group owns one or more sockets. Rings are one group with two
 * interchangeable sockets. Weapons are one group whose capacity depends on
 * what you picked: a two-hander spans both sockets, a shield only fits the
 * second, a plain one-hander fits either. `seatTargets` resolves all of that
 * in one pass so nothing downstream has to special-case a slot ever again.
 *
 * Authored as CommonJS on purpose: webpack bundles it for the frontend and
 * `auth/` requires it directly, so there is exactly one copy of this table.
 * Types for TS consumers live in gearSlots.d.ts next door.
 *
 * The table is keyed by game code. Slot layouts are game-specific (the ranged
 * slot disappears in later expansions, relics replace it for some classes), so
 * new versions are a table entry rather than a code change, and lifting the
 * whole thing into DB config later is a data migration.
 */

// ── Item-slot vocabularies ────────────────────────────────────
// Blizzard's item API and our loot tables disagree on spelling, so every
// variant we have ever seen is listed rather than normalised away.

var TWO_HAND = ['TWO_HAND', 'TWOHAND', 'TWOHWEAPON'];
var MAIN_HAND_ONLY = ['MAIN_HAND', 'MAINHAND', 'WEAPONMAINHAND'];
var OFF_HAND_ONLY = ['OFF_HAND', 'OFFHAND', 'SHIELD', 'HOLDABLE', 'WEAPONOFFHAND'];
var EITHER_HAND = ['ONE_HAND', 'ONEHAND', 'WEAPON'];
var RANGED = ['RANGED', 'RANGED_RIGHT', 'RANGEDRIGHT', 'BOW', 'CROSSBOW', 'GUN', 'WAND', 'THROWN', 'RELIC'];

function concat() {
  var out = [];
  for (var i = 0; i < arguments.length; i++) out = out.concat(arguments[i]);
  return out;
}

/**
 * Fold a raw slot string into the canonical form used everywhere else.
 * Handles hyphens, spaces, casing, and Battle.net's positional suffixes
 * (FINGER_1 / TRINKET_2), which matter to the profile API but never to us:
 * sockets within a group are interchangeable.
 */
function normalizeSlot(raw) {
  var s = String(raw == null ? '' : raw).trim().toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');
  if (s.indexOf('FINGER') === 0) return 'FINGER';
  if (s.indexOf('TRINKET') === 0) return 'TRINKET';
  return s;
}

// ── The table ─────────────────────────────────────────────────
//
// Order is paper-doll order, not DB order. `column` drives the armory
// layout: left rail, right rail, weapons row beneath.
//
// socket.accepts  - item slots that may be PLANNED into this socket
//                   (omitted = anything the group accepts)
// socket.equipSlots - equipment slot_type values that LAND in this socket
//                   (omitted = anything the group accepts)
// group.spans     - item slots that occupy every socket at once

var DEFAULT_GROUPS = [
  { id: 'head', label: 'Head', column: 'left', itemSlots: ['HEAD'], sockets: [{ key: 'head', label: 'Head' }] },
  { id: 'neck', label: 'Neck', column: 'left', itemSlots: ['NECK'], sockets: [{ key: 'neck', label: 'Neck' }] },
  { id: 'shoulder', label: 'Shoulders', column: 'left', itemSlots: ['SHOULDER', 'SHOULDERS'], sockets: [{ key: 'shoulder', label: 'Shoulders' }] },
  { id: 'back', label: 'Back', column: 'left', itemSlots: ['BACK', 'CLOAK'], sockets: [{ key: 'back', label: 'Back' }] },
  { id: 'chest', label: 'Chest', column: 'left', itemSlots: ['CHEST', 'ROBE'], sockets: [{ key: 'chest', label: 'Chest' }] },
  // Cosmetic groups hold no loot rows. They exist so the doll keeps its 8+8
  // symmetry and reads as a character sheet; they never count toward progress.
  { id: 'shirt', label: 'Shirt', column: 'left', cosmetic: true, itemSlots: ['SHIRT', 'BODY'], sockets: [{ key: 'shirt', label: 'Shirt' }] },
  { id: 'tabard', label: 'Tabard', column: 'left', cosmetic: true, itemSlots: ['TABARD'], sockets: [{ key: 'tabard', label: 'Tabard' }] },
  { id: 'wrist', label: 'Wrist', column: 'left', itemSlots: ['WRIST'], sockets: [{ key: 'wrist', label: 'Wrist' }] },

  { id: 'hands', label: 'Hands', column: 'right', itemSlots: ['HAND', 'HANDS'], sockets: [{ key: 'hands', label: 'Hands' }] },
  { id: 'waist', label: 'Waist', column: 'right', itemSlots: ['WAIST'], sockets: [{ key: 'waist', label: 'Waist' }] },
  { id: 'legs', label: 'Legs', column: 'right', itemSlots: ['LEGS'], sockets: [{ key: 'legs', label: 'Legs' }] },
  { id: 'feet', label: 'Feet', column: 'right', itemSlots: ['FEET', 'FOOT'], sockets: [{ key: 'feet', label: 'Feet' }] },
  {
    id: 'ring',
    label: 'Rings',
    column: 'right',
    itemSlots: ['FINGER'],
    sockets: [{ key: 'ring1', label: 'Ring' }, { key: 'ring2', label: 'Ring' }],
  },
  {
    id: 'trinket',
    label: 'Trinkets',
    column: 'right',
    itemSlots: ['TRINKET'],
    sockets: [{ key: 'trinket1', label: 'Trinket' }, { key: 'trinket2', label: 'Trinket' }],
  },

  {
    id: 'weapons',
    label: 'Weapons',
    column: 'bottom',
    itemSlots: concat(TWO_HAND, MAIN_HAND_ONLY, OFF_HAND_ONLY, EITHER_HAND),
    spans: TWO_HAND,
    sockets: [
      {
        key: 'mainhand',
        label: 'Main Hand',
        accepts: concat(TWO_HAND, MAIN_HAND_ONLY, EITHER_HAND),
        equipSlots: concat(TWO_HAND, MAIN_HAND_ONLY),
      },
      {
        key: 'offhand',
        label: 'Off Hand',
        accepts: concat(OFF_HAND_ONLY, EITHER_HAND),
        equipSlots: OFF_HAND_ONLY,
      },
    ],
  },
  { id: 'ranged', label: 'Ranged', column: 'bottom', itemSlots: RANGED, sockets: [{ key: 'ranged', label: 'Ranged' }] },
];

/** Per-game overrides go here. Everything WoW-shaped uses the default table. */
var GROUPS_BY_GAME = {};

function groupsForGame(gameCode) {
  if (gameCode && Object.prototype.hasOwnProperty.call(GROUPS_BY_GAME, gameCode)) {
    return GROUPS_BY_GAME[gameCode];
  }
  return DEFAULT_GROUPS;
}

/** Groups that hold real loot, in doll order. */
function planningGroups(gameCode) {
  return groupsForGame(gameCode).filter(function (g) {
    return !g.cosmetic;
  });
}

function groupById(gameCode, id) {
  var groups = groupsForGame(gameCode);
  for (var i = 0; i < groups.length; i++) if (groups[i].id === id) return groups[i];
  return null;
}

function groupsForColumn(gameCode, column) {
  return groupsForGame(gameCode).filter(function (g) {
    return g.column === column;
  });
}

// ── Membership ────────────────────────────────────────────────

/** Can this group hold an item with the given slot string? */
function groupAcceptsItem(group, rawSlot) {
  return group.itemSlots.indexOf(normalizeSlot(rawSlot)) !== -1;
}

/**
 * Every group that could hold this item. A plain one-hander belongs to
 * exactly one group (weapons) under this model, which is the whole point:
 * main hand and off hand stopped being separate groups.
 */
function groupsForItemSlot(gameCode, rawSlot) {
  var slot = normalizeSlot(rawSlot);
  return groupsForGame(gameCode).filter(function (g) {
    return g.itemSlots.indexOf(slot) !== -1;
  });
}

function socketAcceptsItem(group, socket, rawSlot) {
  var slot = normalizeSlot(rawSlot);
  var list = socket.accepts || group.itemSlots;
  return list.indexOf(slot) !== -1;
}

function socketTakesEquipped(group, socket, rawSlotType) {
  var slot = normalizeSlot(rawSlotType);
  var list = socket.equipSlots || socket.accepts || group.itemSlots;
  return list.indexOf(slot) !== -1;
}

function isSpanningItem(group, rawSlot) {
  if (!group.spans) return false;
  return group.spans.indexOf(normalizeSlot(rawSlot)) !== -1;
}

/** Equipment slot_type strings that could be replaced by an item in this slot. */
function equipSlotsForItemSlot(gameCode, rawSlot) {
  var groups = groupsForItemSlot(gameCode, rawSlot);
  var out = [];
  for (var i = 0; i < groups.length; i++) {
    var g = groups[i];
    for (var j = 0; j < g.sockets.length; j++) {
      var list = g.sockets[j].equipSlots || g.sockets[j].accepts || g.itemSlots;
      for (var k = 0; k < list.length; k++) if (out.indexOf(list[k]) === -1) out.push(list[k]);
    }
  }
  return out;
}

// ── Seating ───────────────────────────────────────────────────

/**
 * Sit an ordered list of saved targets into the group's sockets.
 *
 * Walks in priority order and gives each entry the first socket that will
 * take it. A spanning item (a two-hander) claims every socket, but only if
 * they are all still free - if you already seated a one-hander, the
 * two-hander becomes an alternate instead of silently evicting it.
 *
 * `getSlot` reads the slot string off whatever shape the caller is holding
 * (BIS entries use `.slot`, loot rows use `.slot`, equipped pieces use
 * `.slot_type`), so this works for plans and snapshots alike.
 *
 * Returns one seat per socket, whether a single item covers them all, and
 * anything that had nowhere to sit.
 */
function seatTargets(group, entries, getSlot) {
  var read = getSlot || function (e) {
    return e && e.slot;
  };
  var seats = [];
  for (var s = 0; s < group.sockets.length; s++) seats.push(null);
  var extra = [];
  var spans = false;

  for (var i = 0; i < (entries || []).length; i++) {
    var entry = entries[i];
    var slot = read(entry);

    if (spans) {
      extra.push(entry);
      continue;
    }

    if (isSpanningItem(group, slot)) {
      var allFree = true;
      for (var f = 0; f < seats.length; f++) if (seats[f] !== null) allFree = false;
      if (allFree) {
        for (var w = 0; w < seats.length; w++) seats[w] = entry;
        spans = true;
      } else {
        extra.push(entry);
      }
      continue;
    }

    var placed = false;
    for (var n = 0; n < seats.length; n++) {
      if (seats[n] === null && socketAcceptsItem(group, group.sockets[n], slot)) {
        seats[n] = entry;
        placed = true;
        break;
      }
    }
    if (!placed) extra.push(entry);
  }

  return { seats: seats, spans: spans, extra: extra };
}

/**
 * Same idea for a Battle.net snapshot. Equipped pieces report the socket they
 * are actually in, so they seat by slot_type rather than by what the item is.
 */
function seatEquipped(group, pieces) {
  var seats = [];
  for (var s = 0; s < group.sockets.length; s++) seats.push(null);
  var extra = [];

  for (var i = 0; i < (pieces || []).length; i++) {
    var piece = pieces[i];
    var slot = normalizeSlot(piece && piece.slot_type);
    var placed = false;
    for (var n = 0; n < seats.length; n++) {
      if (seats[n] === null && socketTakesEquipped(group, group.sockets[n], slot)) {
        seats[n] = piece;
        placed = true;
        break;
      }
    }
    // A two-hander reports MAIN_HAND, so it seats in socket 0 and leaves the
    // off hand empty - which is the truth for a snapshot.
    if (!placed) extra.push(piece);
  }

  return { seats: seats, extra: extra };
}

/** Everything in `pieces` that belongs to this group, in socket order. */
function equippedForGroup(group, pieces) {
  return (pieces || []).filter(function (p) {
    var slot = normalizeSlot(p && p.slot_type);
    if (group.itemSlots.indexOf(slot) !== -1) return true;
    for (var i = 0; i < group.sockets.length; i++) {
      if (socketTakesEquipped(group, group.sockets[i], slot)) return true;
    }
    return false;
  });
}

module.exports = {
  normalizeSlot: normalizeSlot,
  groupsForGame: groupsForGame,
  planningGroups: planningGroups,
  groupById: groupById,
  groupsForColumn: groupsForColumn,
  groupAcceptsItem: groupAcceptsItem,
  groupsForItemSlot: groupsForItemSlot,
  socketAcceptsItem: socketAcceptsItem,
  socketTakesEquipped: socketTakesEquipped,
  isSpanningItem: isSpanningItem,
  equipSlotsForItemSlot: equipSlotsForItemSlot,
  seatTargets: seatTargets,
  seatEquipped: seatEquipped,
  equippedForGroup: equippedForGroup,
  DEFAULT_GROUPS: DEFAULT_GROUPS,
};
